import { Button, NumericInput } from '@blueprintjs/core';
import React, { useEffect, useState } from 'react';
import { capitalize, numberRange } from 'utils';
import {
  ClimberCategory,
  RouteCategory,
  ROUTE_CATEGORIES,
} from '../../constants';

import deepEqual from 'deep-equal';
import { useAppDispatch, useAppSelector } from 'hooks';
import { fetchRoutesConfig, updateRoutesConfig } from 'store/registration';
import styles from './styles.module.scss';

type CheckboxIndex = {
  routeCategory: RouteCategory;
  climberCategory: ClimberCategory;
  route: number;
};

export default function RouteNumbersTable() {
  const routeNumbersConfig = useAppSelector(
    (state) => state.registration.routeNumbers
  );

  const [draftConfig, setDraftConfig] = useState<RouteNumbersConfig>();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const untouched = deepEqual(draftConfig, routeNumbersConfig);

  const [lastToggled, setLastToggled] = useState<CheckboxIndex>();

  useEffect(() => {
    setDraftConfig(routeNumbersConfig);
  }, [routeNumbersConfig]);

  useEffect(() => {
    (async function () {
      setLoading(true);
      await dispatch(fetchRoutesConfig());
      setLoading(false);
    })();
  }, []);

  async function onSave() {
    if (!draftConfig) {
      return;
    }

    const filteredConfig = filterRemovedRoutes(draftConfig);

    setLoading(true);
    await dispatch(updateRoutesConfig(filteredConfig));
    setLoading(false);
  }

  function toggleRouteForCategory(
    routeCategory: RouteCategory,
    climberCategory: ClimberCategory,
    route: number,
    enabled: boolean,
    bulkToggle: boolean
  ) {
    let routeIdxs: number[];
    let categories: ClimberCategory[];
    if (bulkToggle && lastToggled?.routeCategory === routeCategory) {
      routeIdxs = numberRange(
        Math.min(route, lastToggled.route),
        Math.max(route, lastToggled.route) + 1
      );

      const currentCategoryIdx =
        Object.keys(ClimberCategory).indexOf(climberCategory);

      const lastCategoryIdx = Object.keys(ClimberCategory).indexOf(
        lastToggled.climberCategory
      );

      const categoryIdxs = numberRange(
        Math.min(currentCategoryIdx, lastCategoryIdx),
        Math.max(currentCategoryIdx, lastCategoryIdx) + 1
      );

      categories = categoryIdxs.map(
        (idx) => Object.keys(ClimberCategory)[idx]
      ) as ClimberCategory[];
    } else {
      routeIdxs = [route];
      categories = [climberCategory];
    }

    const newState = JSON.parse(
      JSON.stringify(draftConfig)
    ) as RouteNumbersConfig;

    categories.forEach((cat) => {
      const existingRoutes = newState.perCategory[routeCategory][cat] || [];

      if (enabled) {
        const newRoutes = Array.from(new Set(existingRoutes.concat(routeIdxs)));
        newRoutes.sort((a, b) => a - b);
        newState.perCategory[routeCategory][cat] = newRoutes;

      } else {
        newState.perCategory[routeCategory][cat] = existingRoutes.filter(
          (existingRoute) => !routeIdxs.includes(existingRoute)
        );
      }
    });

    setDraftConfig(newState);

    setLastToggled({ routeCategory, climberCategory, route });
  }

  if (!draftConfig) {
    return null;
  }

  return (
    <div className={styles.routeNumbersView}>
      {ROUTE_CATEGORIES.map((routeCategory) => {
        return (
          <React.Fragment key={routeCategory}>
            <h2>{capitalize(routeCategory)}</h2>
            <div>
              <span>Total routes:</span>
              <NumericInput
                value={draftConfig['routeCount'][routeCategory]}
                onValueChange={(value) => {
                  setDraftConfig((prev) => ({
                    ...prev!,
                    routeCount: {
                      ...prev!.routeCount,
                      [routeCategory]: value,
                    },
                  }));
                }}
                min={0}
              />
            </div>

            <div className={styles.routeTable}>
              <div className={styles.routeTableIndex}>
                <div className={styles.routeTableLabel}>&nbsp;</div>
                {Object.values(ClimberCategory).map((climberCategory) => {
                  return (
                    <div
                      key={climberCategory}
                      className={styles.routeTableLabel}
                    >
                      {climberCategory}
                    </div>
                  );
                })}
              </div>
              <div className={styles.routeTableIndex}>
                <div className={styles.routeTableLabel}>&nbsp;</div>
                {Object.values(ClimberCategory).map((climberCategory) => {
                  const routeCount =
                    draftConfig.perCategory[routeCategory][climberCategory]
                      ?.length || 0;
                  return (
                    <div
                      key={climberCategory}
                      className={styles.routeTableLabel}
                    >
                      ({routeCount})
                    </div>
                  );
                })}
              </div>

              <div className={styles.routeTableBody}>
                <div className={styles.routeTableRow}>
                  {Array(draftConfig['routeCount'][routeCategory])
                    .fill(0)
                    .map((_, index) => {
                      return (
                        <div className={styles.routeCheckboxLabel} key={index}>
                          {index + 1}
                        </div>
                      );
                    })}
                </div>

                {Object.values(ClimberCategory).map((climberCategory) => {
                  return (
                    <div key={climberCategory} className={styles.routeTableRow}>
                      {Array(draftConfig['routeCount'][routeCategory])
                        .fill(0)
                        .map((_, index) => {
                          const routeIndex = index + 1;
                          const checked =
                            draftConfig.perCategory[routeCategory][
                              climberCategory
                            ]?.includes(routeIndex) || false;
                          return (
                            <input
                              checked={checked}
                              onClick={(e) => {
                                toggleRouteForCategory(
                                  routeCategory,
                                  climberCategory,
                                  routeIndex,
                                  !checked,
                                  e.shiftKey
                                );
                              }}
                              onChange={() => {}}
                              type="checkbox"
                              className={styles.routeCheckbox}
                              key={routeIndex}
                            />
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        );
      })}

      <div className={styles.routesActionButtons}>
        <Button
          disabled={loading || untouched}
          onClick={() => setDraftConfig(routeNumbersConfig)}
        >
          Cancel
        </Button>
        <Button
          intent="primary"
          onClick={onSave}
          disabled={loading || untouched}
          loading={loading}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function filterRemovedRoutes(config: RouteNumbersConfig): RouteNumbersConfig {
  return {
    routeCount: config.routeCount,
    perCategory: Object.keys(config.perCategory).reduce(
      (routeCategoryAcc, routeCat) => {
        const routeCategory = routeCat as RouteCategory;

        routeCategoryAcc[routeCategory] = Object.entries(
          config.perCategory[routeCategory]
        ).reduce((acc, [climberCat, climberCatRoutes]) => {
          const maxRoute = config.routeCount[routeCategory];

          acc[climberCat] = climberCatRoutes.filter(
            (route) => route <= maxRoute
          );
          return acc;
        }, {} as { [climberCategory: string]: number[] });

        return routeCategoryAcc;
      },
      {} as RouteNumbersConfig['perCategory']
    ),
  };
}
